import { isEqual, keys, reduce } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { PANEL_ACCESS, PANEL_FILE, PANEL_MAP, PANEL_MODULES, PANEL_STATISTICS, PANEL_PARAMETERS } from '../../constants/UserConstants'
import UserAccessPanel from './UserAccessPanel'
import UserDetailForm from './UserDetailForm'
import UserModulesPanel from './UserModulesPanel'
import UserStatisticPanel from './UserStatisticPanel'
import MapAccountUserPanel from '../../../../../account/components/panel/MapAccountUserPanel'
import SettingDto from 'administration/dto/SettingDto'
import { Button, Card, Dialog, DialogActions, DialogTitle, Grid, Stack } from '@mui/material'
import { MAP_X_COORDINATE, MAP_Y_COORDINATE, MAP_ZOOM } from 'account/constants/AccountSettingsConstants'
import ProgressBar from 'components/progress/ProgressBar'
import AdministrationAction from 'administration/actions/AdministrationAction'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import UserParameterPanel from './UserParameterPanel'
import AccountAction from 'account/actions/AccountAction'

const UserDetailPanel = ({
    view = PANEL_FILE,
    userLogin = '',
}) => {
    const {
        user,
        userHabilitations,
        userSettings,
    } = useSelector(store => ({
        user: store.UserReducer.user,
        userHabilitations: store.UserReducer.userHabilitations,
        userSettings: store.AdministrationReducer.userSettings,
    }), shallowEqual)

    const [settings, setSettings] = useState(userSettings)
    const [openModalCheck, setOpenModalCheck] = useState(false)

    const dispatch = useDispatch()

    useEffect(() => {
        setSettings(userSettings)
    }, [userSettings])

    useTitle(() => {
        const baseTitle = [{
            title: i18n.administration,
            href: 'administration',
        }, {
            title: i18n.users,
            href: 'administration/user',
        }, {
            title: user.login,
            href: `administration/user/${user.login}`,
        }]
        switch (view) {
            case PANEL_FILE:
                return [ ...baseTitle, {
                    title: i18n.file,
                    href: `administration/user/${user.login}/dashboard`,
                }]
            case PANEL_MODULES:
                return [ ...baseTitle, {
                    title: i18n.modules,
                    href: `administration/user/${user.login}/modules`,
                }]
            case PANEL_STATISTICS:
                return [ ...baseTitle, {
                    title: i18n.statistics,
                    href: `administration/user/${user.login}/statistics`,
                }]
            case PANEL_ACCESS:
                return [ ...baseTitle, {
                    title: i18n.access,
                    href: `administration/user/${user.login}/access`,
                }]
            case PANEL_MAP:
                return [ ...baseTitle, {
                    title: i18n.map,
                    href: `administration/user/${user.login}/map`,
                }]
            case PANEL_PARAMETERS:
                return [ ...baseTitle, {
                    title: i18n.parameters,
                    href: `administration/user/${user.login}/parameters`,
                }]
            default:
                return [
                    ...baseTitle,
                ]
        }
    }, [view, user.login])

    const onSave = (settingsToSave, cb = () => {}) => {
        if (!isEqual(userSettings, settingsToSave)) {
            const formattedSettings = settingsToSave.map(o => ({
                ...o,
                value: o.value ? o.value.toString() : '',
            }))
            dispatch(AdministrationAction.resetUserSettings())
            dispatch(AccountAction.updateSettings(user.login, formattedSettings, false, cb))
        }
    }

    useActions(() => (view === PANEL_MAP && user.consultant !== '1') && ({
        save: () => onSave(settings),
    }), [user, settings])

    const listOfHabilitations = useMemo(() => {
        const result = reduce(userHabilitations, (a, b) => {
            (a[b.habilitation.split('_')[0]] || (a[b.habilitation.split('_')[0]] = [])).push(b)
            return a
        }, {})
        return keys(result).map(o => ({
            module: o,
            values: result[o],
        }))
    }, [userHabilitations])

    const onChangeSettings = newSettings => {
        const settingsFiltered = settings.filter(s => !newSettings.some(ns => s.parameter === ns.parameter))
        const newSettingsFormatted = [...settingsFiltered, ...newSettings].map(o => new SettingDto({
            ...o,
            login: user.login,
        }))
        setSettings(newSettingsFormatted)
    }

    const onRemoveCoordinates = () => {
        onSave(settings.filter(s => ![MAP_X_COORDINATE, MAP_Y_COORDINATE, MAP_ZOOM].includes(s.parameter)), () => setOpenModalCheck(prev => !prev))
    }

    switch (view) {
        case PANEL_FILE: default:
            return <UserDetailForm userLogin={userLogin} />
        case PANEL_MODULES:
            return <UserModulesPanel listOfHabilitations={ listOfHabilitations } />
        case PANEL_ACCESS:
            return <UserAccessPanel />
        case PANEL_STATISTICS:
            return <UserStatisticPanel />
        case PANEL_PARAMETERS:
            return <UserParameterPanel />
        case PANEL_MAP:
            return userSettings.length > 0 ? (
                <Card>
                    <Stack spacing={2} sx={{ padding: 2 }}>
                        <MapAccountUserPanel
                            accountUser={user}
                            changeSettings={onChangeSettings}
                            accountUserSettings={settings}
                            mapHeight={625}
                        />
                        <Grid container justifyContent='flex-end'>
                            <Button variant='contained' color='primary' onClick={() => setOpenModalCheck(prev => !prev)}>
                                {i18n.clearCoordinatesToUseDefault}
                            </Button>
                        </Grid>
                    </Stack>
                    <Dialog
                        onClose={() => setOpenModalCheck(false)}
                        open={openModalCheck}
                    >
                        <DialogTitle>{i18n.sureDeleteCoordinates}</DialogTitle>
                        <DialogActions>
                            <Button onClick={() => setOpenModalCheck(false)} variant='outlined' >
                                {i18n.close}
                            </Button>
                            <Button onClick={onRemoveCoordinates} variant='contained' color='primary'>
                                {i18n.validate}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Card>
            ) : (
                <ProgressBar />
            )
    }
}

UserDetailPanel.propTypes = {
    view: PropTypes.string,
    userLogin: PropTypes.string,
}

export default UserDetailPanel
